import { CircleCheck, CircleSlash, Clock, type LucideIcon } from "lucide-react";
import type {
  Attitude,
  Chapter,
  ExerciseKind,
  Language,
  ListStatus,
  MarkStatus,
  ModerationStatus,
  ReadingSource,
} from "@/shared/api/book";

/** A hozzáállás-kategóriák a forrásban mondatkezdő nagybetűvel (design-rendszer 3.). */
export const ATTITUDE_LABEL: Record<Attitude, string> = {
  tudatosito: "Tudatosító",
  tanulmanyozo: "Tanulmányozó",
  onfejleszto: "Önfejlesztő",
  alkoto: "Alkotó",
};

export const ATTITUDES = Object.keys(ATTITUDE_LABEL) as Attitude[];

export const KIND_LABEL: Record<ExerciseKind, string> = {
  exercise: "Gyakorlat",
  meditation: "Meditáció",
};

/** A gyakorlat jelölései. A harmadik a kódban `not_for_me`, a felületen 2026-09-21 óta
 *  „Kihagyom" — ugyanaz a szó, mint az Ajánlóban. */
export const MARK_LABEL: Record<MarkStatus, string> = {
  want_to_try: "Tervezem kipróbálni",
  tried: "Kipróbáltam",
  not_for_me: "Kihagyom",
};

/** A jelölések ikonja (a kedvenc szívéhez hasonlóan): óra = tervezem, pipa = megvolt,
 *  áthúzott kör = kihagyom. Egy ikon egy jelentés (design-rendszer 5.): a `target`
 *  szándékosan **nem** ez, mert az a Gyakorlattár nav-ikonja. */
export const MARK_ICON: Record<MarkStatus, LucideIcon> = {
  want_to_try: Clock,
  tried: CircleCheck,
  not_for_me: CircleSlash,
};

/** Az Ajánló jelölései — mobilon és asztalin ugyanaz a rövid szó; az ikonok a gyakorlatéi. */
export const LIST_LABEL: Record<ListStatus, string> = {
  saved: "Tervezem",
  done: "Megnéztem",
  skipped: "Kihagyom",
};

export const LIST_ICON: Record<ListStatus, LucideIcon> = {
  saved: MARK_ICON.want_to_try,
  done: MARK_ICON.tried,
  skipped: MARK_ICON.not_for_me,
};

export const SOURCE_LABEL: Record<ReadingSource, string> = {
  authors: "A szerzőktől",
  community: "A közösségtől",
};

export const LANGUAGE_LABEL: Record<Language, string> = { hu: "Magyar", en: "Angol" };

export const LANGUAGES = Object.keys(LANGUAGE_LABEL) as Language[];

/** A fejezet-szűrő opciói: fő- és alfejezetek egy listában, sorszámmal. */
export function chapterOptions(chapters: Chapter[]): Array<{ value: number; label: string }> {
  return chapters
    .flatMap((c) => [c, ...c.children])
    .map((c) => ({ value: c.id, label: `${c.number}. ${c.title}` }));
}

export const STATUS_LABEL: Record<ModerationStatus, string> = {
  pending: "Jóváhagyásra vár",
  approved: "Jóváhagyva",
  rejected: "Elutasítva",
};

const MONTHS = [
  "január", "február", "március", "április", "május", "június",
  "július", "augusztus", "szeptember", "október", "november", "december",
];

/** „augusztus 22." — a design-rendszer 9. szerinti dátumforma. */
export function formatDay(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${MONTHS[d.getMonth()]} ${d.getDate()}.`;
}

export function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}. ${MONTHS[d.getMonth()]} ${d.getDate()}.`;
}

const pad = (n: number) => String(n).padStart(2, "0");

/** „2026. október 7. 14:05" — ahol a pontos idő is számít (az Ajánló admin-listája, mert
 *  az a „Felkerült” időpont szerint rendez). */
export function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${formatDate(iso)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** ISO időpont → a `datetime-local` mező helyi idejű „ÉÉÉÉ-HH-NNTÓÓ:PP” értéke. */
export function toLocalInput(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** A `datetime-local` mező helyi idejű értéke → ISO (UTC); üres vagy hibás → null. */
export function fromLocalInput(value: string): string | null {
  const d = new Date(value);
  return value && !Number.isNaN(d.getTime()) ? d.toISOString() : null;
}

/** „ma" / „tegnap" / „3 napja" / „2 hete" — friss elemnél beszédesebb a pontos dátumnál;
 *  egy hónapon túl a dátumot adja vissza. */
export function relativeDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const days = Math.floor((Date.now() - d.getTime()) / 86_400_000);
  if (days <= 0) return "ma";
  if (days === 1) return "tegnap";
  if (days < 7) return `${days} napja`;
  if (days < 30) return `${Math.floor(days / 7)} hete`;
  return formatDate(iso);
}

/** „12:30" — a meditáció hossza. */
export function formatDuration(seconds: number | null | undefined): string {
  if (!seconds || seconds < 0) return "";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

/** Számlálók középső ponttal: „2 gyakorlat · 5 fogalom" (design-rendszer 9.). */
export function countLine(parts: Array<[number, string]>): string {
  return parts
    .filter(([n]) => n > 0)
    .map(([n, label]) => `${n} ${label}`)
    .join(" · ");
}

/** Az ajánlás beküldőjének neve közösségi elemnél; törölt fióknál „egy olvasó". */
export function submitterName(author: string | null): string {
  return author ?? "egy olvasó";
}

/** Az ajánlás személyes részének címkéje a kártyán és a moderációs soron (a „Miért ajánlod,
 *  mit adott neked?" válasz felett): a szerzőinél „A szerzők ajánlása:", a közösséginél a
 *  beküldő nevével. A forrás dönt, nem a név megléte. */
export function recommendLabel(source: ReadingSource, author: string | null): string {
  if (source === "authors") return "A szerzők ajánlása:";
  return author ? `${author} ajánlása:` : "Egy olvasó ajánlása:";
}
